import { useState } from "react";
import { Check, ExternalLink, FileX } from "lucide-react";
import { Link, useParams } from "react-router";

import { getFraktionLabel } from "@/components/atomic-crm/misc/fraktionen";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltPageHeader } from "../ui/VoltPageHeader";
import { VoltStatusBadge } from "../ui/VoltStatusBadge";
import {
  salesName,
  useClosingDetail,
  useClosingPdfUrl,
  useVoltRoles,
} from "./attendanceApi";
import {
  countEntries,
  formatDate,
  KIND_LABELS,
  monthLabel,
} from "./attendanceLogic";
import { AttendanceAccess, AttendanceFrame } from "./AttendanceFrame";
import {
  CLOSING_STATUS_MAP,
  closingLists,
  closingSteps,
  listCountText,
  type ClosingList,
  type ClosingStep,
} from "./closingLogic";

type Detail = NonNullable<ReturnType<typeof useClosingDetail>["data"]>;

const Steps = ({ steps }: { steps: ClosingStep[] }) => (
  <ol className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    {steps.map((s, i) => (
      <li
        key={s.label}
        className={cn(
          "flex flex-col gap-1 rounded-lg px-4 py-3",
          s.done
            ? "bg-primary text-primary-foreground"
            : "border border-dashed border-muted-foreground/60",
        )}
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold tracking-wide">
          {s.done ? <Check aria-hidden className="size-3.5" /> : null}
          {i + 1} · {s.done ? "ERLEDIGT" : "OFFEN"}
        </span>
        <span className="font-semibold">{s.label}</span>
        <span
          className={cn(
            "text-sm",
            s.done ? "opacity-80" : "text-muted-foreground",
          )}
        >
          {s.sub}
        </span>
      </li>
    ))}
  </ol>
);

/** Phone browsers do not show PDFs inline reliably; there the button opens it. */
const Preview = ({ list }: { list: ClosingList }) => {
  const url = useClosingPdfUrl(list.path);
  if (url.isPending) return <Skeleton className="h-10 w-full md:h-[70vh]" />;
  if (url.error || !url.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Das PDF konnte nicht geladen werden.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <iframe
        title={list.file}
        src={url.data}
        className="hidden h-[70vh] w-full rounded-md border bg-white md:block"
      />
      <Button variant="outline" asChild className="self-start">
        <a href={url.data} target="_blank" rel="noreferrer">
          <ExternalLink aria-hidden />
          PDF öffnen
        </a>
      </Button>
    </div>
  );
};

const ClosingDetail = ({ detail }: { detail: Detail }) => {
  const { closing, sessions, entries, settings, signatory } = detail;
  const lists = closingLists(closing, sessions, entries);
  const [picked, setPicked] = useState<string | null>(null);
  const shown = lists.find((l) => l.kind === picked) ?? lists[0];
  const open = countEntries(entries).open;
  const steps = closingSteps({
    closing,
    open,
    signatoryName: signatory ? salesName(signatory) : null,
    signatoryFunction:
      settings?.signatory_function ?? "Fraktionsgeschäftsführer",
    live: settings?.live ?? false,
  });

  return (
    <>
      <VoltPageHeader
        title={`Monatsabschluss ${monthLabel(closing.month.slice(0, 7))}`}
        description={`Fraktion ${getFraktionLabel(closing.district)} · Version ${closing.version}`}
        action={
          <VoltStatusBadge status={closing.status} map={CLOSING_STATUS_MAP} />
        }
      />
      <Steps steps={steps} />
      {open ? (
        <Alert className="mb-6">
          <AlertTitle>
            {open === 1
              ? "1 Eintrag steht noch auf offen."
              : `${open} Einträge stehen noch auf offen.`}
          </AlertTitle>
          <AlertDescription>
            Sie halten nichts auf, stehen so aber in der Liste. Ändern lässt
            sich der Status in der jeweiligen Sitzung.
          </AlertDescription>
        </Alert>
      ) : null}
      {!shown ? (
        <VoltEmpty
          icon={FileX}
          title="Die Listen sind noch nicht fertig"
          description="Sie entstehen beim nächsten Lauf des Monatsabschlusses, spätestens eine Stunde später."
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
          <Card className="self-start">
            <CardContent className="flex flex-col gap-2">
              <p className="text-xs font-semibold tracking-wide text-muted-foreground">
                ANHÄNGE · ANTIPPEN FÜR VORSCHAU
              </p>
              {lists.map((l) => (
                <Button
                  key={l.kind}
                  variant={l.kind === shown.kind ? "default" : "outline"}
                  aria-pressed={l.kind === shown.kind}
                  className="h-auto min-h-12 flex-col items-start gap-0.5 whitespace-normal text-left"
                  onClick={() => setPicked(l.kind)}
                >
                  <span className="break-all text-sm font-medium">
                    {l.file}
                  </span>
                  <span className="text-xs opacity-80">
                    {KIND_LABELS[l.kind]} · {listCountText(l)}
                  </span>
                </Button>
              ))}
              <div className="mt-2 flex flex-col gap-1 text-sm">
                {sessions.map((s) => (
                  <Link
                    key={s.id}
                    to={`/attendance/sessions/${s.id}`}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {formatDate(s.starts_at)} · {KIND_LABELS[s.kind]}
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
          <Preview key={shown.path} list={shown} />
        </div>
      )}
    </>
  );
};

export const AttendanceClosingPage = () => {
  const { id } = useParams();
  const closingId = Number(id);
  const { ready } = useVoltRoles();
  const detail = useClosingDetail(closingId);
  const closing = detail.data?.closing;
  const back = closing
    ? `/attendance?month=${closing.month.slice(0, 7)}`
    : "/attendance";
  return (
    <AttendanceFrame
      title={
        closing
          ? `Abschluss ${monthLabel(closing.month.slice(0, 7))}`
          : "Monatsabschluss"
      }
      back={back}
    >
      <AttendanceAccess>
        {() =>
          !ready || detail.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : detail.error ? (
            <p role="alert" className="text-sm text-destructive">
              Der Monatsabschluss konnte nicht geladen werden.
            </p>
          ) : !detail.data ? (
            <VoltEmpty
              icon={FileX}
              title="Monatsabschluss nicht gefunden"
              description="Es gibt ihn nicht, oder er gehört zu einer anderen Fraktion."
              action={
                <Button variant="outline" asChild>
                  <Link to="/attendance">Zur Übersicht</Link>
                </Button>
              }
            />
          ) : (
            <ClosingDetail detail={detail.data} />
          )
        }
      </AttendanceAccess>
    </AttendanceFrame>
  );
};

AttendanceClosingPage.path = "/attendance/closings/:id";
